import React, { useCallback, useMemo, useState } from 'react'
import { generatePath, Link } from 'react-router-dom'
import classnames from 'classnames'
import message from 'antd/lib/message'

import { apiManager, dateFormatter } from 'helpers'
import { copyLinkToClipboard, getSocialHandle } from 'helpers/utils'
import { ROUTES, WAITLIST_STATUS } from 'helpers/constants'
import { backdropFilter } from 'helpers/browser'
import { APPLICATION_STATUS_ACTIONS } from 'constants/application-status'

import { HeartIcon, LikesModal, Points, ReplyIcon, TimesIcon } from 'components'
import PhotoGallery from 'components/ProfileInfo/components/PhotoGallery'
import ProfileTags from 'components/ProfileInfo/components/ProfileTags'
import ProfileImage from 'components/ProfileImage'
import UserUpdateModal from 'pages/admin/ForYouProfiles/UserUpdateModal'

import { getStyles } from 'pages/admin/ForYouProfiles/styles'
import Button from 'components/Button'

const IconButton = ({ className, Icon, iconClassName, ...rest }) => (
  <button
    className={classnames(
      'flex items-center justify-center rounded-full bg-white bg-opacity-10 hover:bg-opacity-20',
      className
    )}
    {...rest}
  >
    <Icon className={iconClassName} />
  </button>
)

const UserInfoDobleRow = ({ firstFieldName, firstValue, secondFieldName, secondValue }) => {
  return (
    <div className="w-full flex text-xs mt-3 text-white justify-between">
      <div>
        <span className="font-bold">{`${firstFieldName}: `}</span>
        {firstValue}
      </div>
      <div>
        <span className="font-bold">{`${secondFieldName}: `}</span>
        {secondValue}
      </div>
    </div>
  )
}

const InfoRow = ({ firstValue, secondValue, classes = {}, classes2 = {} }) => {
  return (
    <div className="w-full flex text-xs text-white justify-between">
      <span className={classes}>{firstValue}</span>
      <span className={classes2}>{secondValue}</span>
    </div>
  )
}

const SimpleInfoRow = ({ title, body }) => {
  return (
    <div className="w-full mt-3 text-white" style={{ maxHeight: '100px', overflowY: 'auto' }}>
      <span className="font-bold">{title}</span>
      <p className="mt-0 pt-0  text-xs">{body}</p>
    </div>
  )
}

const IconButtonMd = props => (
  <IconButton
    className="w-8 h-8 md:w-11 md:h-11"
    style={backdropFilter('blur(24.5714px)')}
    iconClassName="w-3 h-3 text-white"
    {...props}
  />
)

const IconButtonLg = ({ iconWhite, ...rest }) => (
  <IconButton
    className="w-16 h-16 md:w-20 md:h-20"
    style={backdropFilter('blur(12.9965px)')}
    iconClassName={classnames('w-9 h-9', {
      'text-azureBlue': !iconWhite,
      'text-white': iconWhite,
    })}
    {...rest}
  />
)

const Info = ({ user, age, profileLink }) => (
  <Link className="text-white hover:text-frolyPink" to={profileLink} target="_blank">
    {user.nftImage && (
      <img src={user.nftImage} alt="nft" className="w-16 h-16 mb-2 object-cover rounded-md" />
    )}
    <div className="text-3xl font-ownersWide font-bold mb-1.5">
      <span>{user.name}</span>
      {age && <span className="text-lightGray text-lg"> {age}</span>}
    </div>
    {user.profile?.bio && (
      <div className="text-sm opacity-70 mb-6 text-lightGray break-words hidden md:block">
        {user.profile.bio}
      </div>
    )}
  </Link>
)

const Profile = ({ admin, user, md, onLike, onAction }) => {
  const [loader, setLoader] = useState({ loading: false, liked: false })
  const [showLikesModal, setShowLikesModal] = useState(false)
  const [showUserUpdateModal, setShowUserUpdateModal] = useState(false)

  const styles = useMemo(() => getStyles(md), [md])
  const age = useMemo(() => dateFormatter.getAgeFromBirthdate(user.birthdate), [user.birthdate])

  const profileLink = useMemo(
    () => generatePath(ROUTES.PUBLIC_PROFILE, { slug: user.username || user.id }),
    [user]
  )

  const ImageRenderer = useMemo(
    () =>
      ({ className, children, ...rest }) =>
        (
          <div className="w-full relative overflow-hidden" style={styles.imageGallery}>
            <ProfileImage
              className={classnames('absolute left-0 top-0 w-full h-full object-cover', className)}
              {...rest}
            />
            <div className="absolute z-5 h-full w-full inset-0 bg-black bg-opacity-10 md:bg-transparent" />
            {children}
          </div>
        ),
    [styles]
  )

  const handleProfileLinkCopy = useCallback(() => {
    const url = `${window.location.origin}${profileLink}`
    copyLinkToClipboard(url)
  }, [profileLink])

  const handleProfileLike = useCallback(
    async ({ liked }) => {
      if (liked) {
        return setShowLikesModal(true)
      }

      try {
        await apiManager.likeProfile(user.id, { liked })
      } catch (error) {
        message.error(error?.message || 'An error occurred, please try again')
      }
    },
    [user]
  )

  const handleLikesModalResponse = useCallback(
    liked => {
      setShowLikesModal(false)
      liked && onLike()
    },
    [onLike]
  )

  const handleLikeAction = useCallback(
    async liked => {
      setLoader({ loading: true, liked })

      const action = onAction || handleProfileLike
      const completed = await action({ liked, user })

      setTimeout(
        () => {
          setLoader({ loading: false, liked })
          completed && onLike()
        },
        admin ? 200 : 400
      )
    },
    [admin, user, onLike, onAction, handleProfileLike]
  )

  const creditCardEntered = appStatuses => {
    const enteredCard = appStatuses.find(
      el => el.action === APPLICATION_STATUS_ACTIONS.SAVE_PAYMENT && el.completed
    )
    return enteredCard
  }

  return (
    <>
      <div className="rounded-lg relative w-full flex overflow-x-hidden md:rounded-2xl">
        <div className="w-full overflow-y-hidden md:w-1/2">
          <PhotoGallery
            images={user.images}
            navPosition={md ? 'top' : 'bottom'}
            pagination={true}
            ImageRenderer={ImageRenderer}
          />
          {!md && (
            <div className="flex flex-col w-full absolute z-10 bottom-20 px-2">
              <Info age={age} user={user} profileLink={profileLink} />
            </div>
          )}
        </div>
        {md && (
          <div className="w-1/2 flex flex-col justify-center p-4 bg-woodsmokeBlack2 overflow-x-hidden">
            <Info age={age} user={user} profileLink={profileLink} />
            {typeof user.points !== 'undefined' && (
              <div className="flex mt-1 mb-1">
                <Points points={user?.points} className="h-8" />
              </div>
            )}
            <div className="w-full mt-4 -mr-4">
              <ProfileTags user={user} />
            </div>

            <InfoRow firstValue={user.email} secondValue={user.phoneNumber} />

            <InfoRow
              firstValue={
                creditCardEntered(user.applicationStatuses)
                  ? 'Credit card entered'
                  : 'Credit card not entered'
              }
              secondValue={
                user.applicationStatuses.every(aps => aps.completed)
                  ? 'Application submitted'
                  : 'Application not submited'
              }
              classes={`${
                creditCardEntered(user.applicationStatuses) ? 'text-white' : 'text-orange'
              }`}
              classes2={`${
                user.applicationStatuses.every(aps => aps.completed === true)
                  ? 'text-white'
                  : 'text-orange'
              }`}
            />

            <UserInfoDobleRow
              firstFieldName="Username"
              firstValue={`@${user.username}`}
              secondFieldName="Instagram"
              secondValue={getSocialHandle(user.instagramLink)}
            />

            <UserInfoDobleRow
              firstFieldName="Gender"
              firstValue={user?.gender}
              secondFieldName="Looking for"
              secondValue={user.genderPreference}
            />

            <SimpleInfoRow
              title="What in your life are you most grateful for:"
              body={user.profile?.gratefulFor}
            />

            <SimpleInfoRow title="Manifest your dream date:" body={user.profile?.dreamDate} />
            {admin && (
              <div className="w-full mt-10">
                <Button
                  className="bg-white text-base ring ring-black h-10 w-full"
                  textColor="text-black"
                  onClick={() => setShowUserUpdateModal(true)}
                >
                  Update User
                </Button>
              </div>
            )}
          </div>
        )}

        {loader.loading && (
          <div className="absolute z-10 h-full w-full bg-black bg-opacity-30 flex items-center justify-center">
            {!admin && (
              <div className="animate-ping-slow transform scale-150">
                <IconButtonLg
                  Icon={loader.liked ? HeartIcon : TimesIcon}
                  iconWhite={!loader.liked}
                />
              </div>
            )}
          </div>
        )}
      </div>
      {![WAITLIST_STATUS.YES, WAITLIST_STATUS.SUBSCRIPTION_PAUSED].includes(
        user.waitlistStatus
      ) && (
        <div className="flex items-center justify-center z-10 w-full space-x-4 h-24 md:h-20 md:-mt-10">
          <IconButtonMd
            Icon={TimesIcon}
            onClick={() => handleLikeAction(false)}
            disabled={loader.loading}
          />
          <IconButtonLg
            Icon={HeartIcon}
            onClick={() => handleLikeAction(true)}
            disabled={loader.loading}
          />
          <IconButtonMd Icon={ReplyIcon} onClick={handleProfileLinkCopy} />
        </div>
      )}
      <LikesModal show={showLikesModal} onClose={handleLikesModalResponse} profile={user} />
      <UserUpdateModal
        user={user}
        show={showUserUpdateModal}
        onComplete={() => {}}
        onClose={() => setShowUserUpdateModal(false)}
      />
    </>
  )
}

export default Profile
